import { useT } from "../../i18n";
import React, { useLayoutEffect, useRef, useState } from "react";
import { Icon, IconCircle, TrendBadge, Sparkline, SearchInput, Dropdown, ViewToggle, ActionMenu, initialsOf, money0 } from "./kit";
import { Sk, SkRows, SkStat } from "../../components/Skeleton";

/* ════════════════════════════════════════════════════════════════════════
   Named, reusable components for the Clients dashboard + Client detail pages.
   ════════════════════════════════════════════════════════════════════════ */

/* ── DashboardStatCard ────────────────────────────────────────────────── */
/* Three-state loading (2026-07-18): while `loading`, the value/trend/spark
 * areas show shimmer blocks in the SAME layout slots — the card never reads
 * "0" before the data exists; the real value fades in on arrival. */
export function DashboardStatCard({ tone = "blue", icon, label, value, sub, trend, spark, sparkColor, clock, testId, loading = false }) {
  return (
    <div className="cx-card cx-stat">
      <div className="cx-stat-top">
        <IconCircle tone={tone} round>{icon}</IconCircle>
        {!loading && trend ? <TrendBadge trend={trend} /> : null}
      </div>
      <div className="cx-stat-label">{label}</div>
      <div className="cx-stat-value" data-testid={testId} data-loading={loading || undefined}>
        {loading ? <SkStat w={56} h={26} /> : <span className="lhc-reveal">{value}</span>}
      </div>
      <div className="cx-stat-sub">{sub}</div>
      {clock ? clock : loading
        ? <Sk className="cx-stat-spark" w={76} h={30} r={7} />
        : <Sparkline points={spark} stroke={sparkColor} />}
    </div>
  );
}

/* ── analog clock dial (local-time stat card) ─────────────────────────── */
export function ClockDial({ hh = 0, mm = 0 }) {
  const minA = mm * 6, hrA = (hh % 12) * 30 + mm * 0.5;
  const hand = (deg, len) => ({ x: 25 + len * Math.sin((deg * Math.PI) / 180), y: 25 - len * Math.cos((deg * Math.PI) / 180) });
  const h = hand(hrA, 10), m = hand(minA, 15);
  const ticks = Array.from({ length: 12 }, (_, i) => { const a = i * 30 * Math.PI / 180; return { x1: 25 + 20 * Math.sin(a), y1: 25 - 20 * Math.cos(a), x2: 25 + 22 * Math.sin(a), y2: 25 - 22 * Math.cos(a) }; });
  return (
    <svg className="cx-stat-clock" viewBox="0 0 50 50">
      <circle cx="25" cy="25" r="23" fill="none" stroke="var(--cx-amber)" strokeWidth="1" strokeDasharray="1.5 3" opacity="0.6" />
      {ticks.map((t, i) => <line key={i} {...t} stroke="var(--cx-faint)" strokeWidth="1" opacity="0.5" />)}
      <line x1="25" y1="25" x2={h.x} y2={h.y} stroke="var(--cx-ink)" strokeWidth="2" strokeLinecap="round" />
      <line x1="25" y1="25" x2={m.x} y2={m.y} stroke="var(--cx-amber)" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="25" cy="25" r="1.6" fill="var(--cx-ink)" />
    </svg>
  );
}

/* ── RevenueChart (smooth SVG area chart) ─────────────────────────────── */
function niceNum(v) { if (v <= 0) return 1; const pow = Math.pow(10, Math.floor(Math.log10(v))); const n = v / pow; return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * pow; }
function smoothPath(pts) {
  if (pts.length < 2) return pts.length ? `M${pts[0].x},${pts[0].y}` : "";
  let d = `M${pts[0].x},${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i], p1 = pts[i], p2 = pts[i + 1], p3 = pts[i + 2] || p2;
    const c1x = p1.x + (p2.x - p0.x) / 6, c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6, c2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C${c1x.toFixed(1)},${c1y.toFixed(1)} ${c2x.toFixed(1)},${c2y.toFixed(1)} ${p2.x},${p2.y}`;
  }
  return d;
}
const fmtAxis = (v) => (v >= 1000 ? `€${(v / 1000)}k` : `€${v}`);

export function RevenueChart({ data = [], mini = false, height }) {
  const wrapRef = useRef(null);
  const [w, setW] = useState(mini ? 290 : 640);
  useLayoutEffect(() => {
    const m = () => { if (wrapRef.current) setW(Math.max(160, wrapRef.current.clientWidth)); };
    m(); window.addEventListener("resize", m); return () => window.removeEventListener("resize", m);
  }, []);
  const H = height || (mini ? 96 : 210);
  const padL = mini ? 6 : 36, padR = mini ? 6 : 14, padT = mini ? 10 : 26, padB = 22;
  const vals = data.map((d) => Number(d.value || 0));
  const rawMax = Math.max(1, ...vals);
  const step = niceNum(rawMax / 3);
  const niceMax = Math.max(step, Math.ceil(rawMax / step) * step);
  const innerW = w - padL - padR, innerH = H - padT - padB;
  const n = data.length || 1;
  const xAt = (i) => padL + (n === 1 ? innerW / 2 : (i / (n - 1)) * innerW);
  const yAt = (v) => padT + innerH - (v / niceMax) * innerH;
  const pts = data.map((d, i) => ({ x: xAt(i), y: yAt(Number(d.value || 0)), v: Number(d.value || 0), label: d.label }));
  const linePath = smoothPath(pts);
  const areaPath = pts.length ? `${linePath} L${pts[pts.length - 1].x},${padT + innerH} L${pts[0].x},${padT + innerH} Z` : "";
  const gridLevels = [];
  for (let v = 0; v <= niceMax + 0.5; v += step) gridLevels.push(v);

  return (
    <div ref={wrapRef} style={{ width: "100%" }}>
      <svg className="cx-chart" width={w} height={H} viewBox={`0 0 ${w} ${H}`}>
        <defs>
          <linearGradient id="cxAreaGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2D7FF9" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#2D7FF9" stopOpacity="0" />
          </linearGradient>
        </defs>
        {!mini && gridLevels.map((v, i) => (
          <g key={i}>
            <line className="cx-chart-grid-line" x1={padL} y1={yAt(v)} x2={w - padR} y2={yAt(v)} />
            <text className="cx-chart-axis" x={padL - 6} y={yAt(v) + 3} textAnchor="end">{fmtAxis(v)}</text>
          </g>
        ))}
        {areaPath && <path className="cx-chart-area" d={areaPath} />}
        {linePath && <path className="cx-chart-line" d={linePath} />}
        {!mini && pts.length > 1 && (
          <line className="cx-chart-marker" x1={pts[pts.length - 1].x} y1={padT} x2={pts[pts.length - 1].x} y2={padT + innerH} />
        )}
        {pts.map((p, i) => {
          const txt = money0(p.v);
          const pw = Math.max(38, txt.length * 6.6 + 12);
          const pcx = Math.min(Math.max(p.x, pw / 2 + 2), w - pw / 2 - 2);
          return (
            <g key={i}>
              {/* trust pass 2026-07-18: value pills only where value EXISTS — a
                  row of "€0" pills on quiet months read as noise, not data */}
              {!mini && p.v > 0 && (
                <>
                  <rect className="cx-chart-pill" x={pcx - pw / 2} y={p.y - 22} width={pw} height="16" rx="8" />
                  <text className="cx-chart-pill-tx" x={pcx} y={p.y - 11} textAnchor="middle">{txt}</text>
                </>
              )}
              <circle className="cx-chart-dot" cx={p.x} cy={p.y} r={mini ? 2.4 : 3.6} />
              <text className="cx-chart-axis" x={p.x} y={H - 6} textAnchor="middle">{p.label}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/* ── TopClientsCard (also used for "Top projects by value") ───────────── */
export function TopClientsCard({ title = "Top clients", icon, items = [], ranked = false, onItem, onViewAll, viewAllLabel = "View all clients", emptyHint = "No data yet.", loading = false }) {
  const max = items.reduce((m, x) => Math.max(m, Number(x.value || 0)), 0) || 1;
  return (
    <div className="cx-card cx-top">
      <div className="cx-top-head">
        <span className="cx-top-ic">{icon || Icon.trophy(17)}</span>
        <span className="cx-top-t">{title}</span>
      </div>
      <div className="cx-top-list lhc-reveal" key={loading ? "sk" : "live"}>
        {loading ? (
          <SkRows n={5} h={28} gap={13} r={7} />
        ) : items.length === 0 ? (
          <div style={{ fontSize: 12.5, color: "var(--cx-faint)", padding: "6px 0" }}>{emptyHint}</div>
        ) : items.map((it, i) => (
          <div key={it.id || i} className="cx-top-item" onClick={() => onItem && onItem(it)}>
            <div className="cx-top-item-row">
              {ranked && <span className="cx-top-rank">{i + 1}</span>}
              <span className="cx-top-name">{it.name}</span>
              <span className="cx-top-val">{money0(it.value)}</span>
            </div>
            <div className="cx-top-bar"><div className="cx-top-bar-fill" style={{ width: `${Math.max(4, Math.round((Number(it.value || 0) / max) * 100))}%`, opacity: 1 - i * 0.13 }} /></div>
          </div>
        ))}
      </div>
      {onViewAll && (
        <div className="cx-top-foot">
          <button className="cx-top-viewall" onClick={onViewAll}>{viewAllLabel}{Icon.arrow(13)}</button>
        </div>
      )}
    </div>
  );
}

/* ── ClientCard ───────────────────────────────────────────────────────── */
export function ClientCard({ client, gradient, metrics, metaItems = [], menuOpen, onMenuToggle, menuItems, onOpen }) {
  const t = useT();
  return (
    <div className="cx-card cx-cc" data-testid={`client-card-${client.id}`} onClick={onOpen}>
      <div className="cx-cc-hero" style={{ background: gradient }} />
      <div className="cx-cc-menu">
        <ActionMenu open={menuOpen} onToggle={onMenuToggle} items={menuItems} />
      </div>
      <div className="cx-cc-body">
        <div className="cx-cc-avatar" style={{ background: gradient }}>{initialsOf(client.name)}</div>
        <div className="cx-cc-name">{client.name || "—"}</div>
        <div className="cx-cc-email">{client.email || client.company || ""}</div>
        <div className="cx-cc-meta">
          {metaItems.map((m, i) => <span key={i} className="cx-cc-meta-i">{m.icon}{m.text}</span>)}
        </div>
      </div>
      <div className="cx-cc-stats">
        <div className="cx-cc-stat"><span className="cx-cc-stat-v">{metrics.projects}</span><span className="cx-cc-stat-l">{t("store.projects")}</span></div>
        <div className="cx-cc-stat-div" />
        <div className="cx-cc-stat"><span className="cx-cc-stat-v">{metrics.quotes}</span><span className="cx-cc-stat-l">{t("store.quotes")}</span></div>
        <div className="cx-cc-stat-div" />
        <div className="cx-cc-stat"><span className="cx-cc-stat-v">{metrics.orders}</span><span className="cx-cc-stat-l">{t("store.orders")}</span></div>
      </div>
      <div className="cx-cc-foot">
        <span className="cx-cc-proj">{Icon.folder(13)}{metrics.projects === 0 ? "No projects yet" : `${metrics.projects} project${metrics.projects === 1 ? "" : "s"}`}</span>
        <span className="cx-cc-open">Open{Icon.arrow(13)}</span>
      </div>
    </div>
  );
}

/* ── ClientToolbar (search + filter + sort + view + count) ────────────── */
export function ClientToolbar({ search, onSearch, searchPlaceholder, filterKey, filterOptions, onFilter, sortKey, sortOptions, onSort, view, onView, count, countLabel, searchTestId }) {
  return (
    <div className="cx-toolbar">
      <SearchInput value={search} onChange={onSearch} placeholder={searchPlaceholder} testId={searchTestId} />
      {filterOptions && <Dropdown caption={null} options={filterOptions} value={filterKey} onPick={onFilter} />}
      {sortOptions && <Dropdown caption="Sort by" options={sortOptions} value={sortKey} onPick={onSort} twoLine />}
      <ViewToggle value={view} onChange={onView} />
      <span className="cx-count">
        {count == null ? <Sk w={64} h={12} /> : <span className="lhc-reveal">{count} {countLabel}{count === 1 ? "" : "s"}</span>}
      </span>
    </div>
  );
}

/* ── ClientHeader (detail page) ───────────────────────────────────────── */
export function ClientHeader({ name, gradient, statusLabel = "Active", metaItems = [], actions, loading = false }) {
  if (loading) {
    // skeleton header in the SAME layout slots — never the "—" / "?" ghost client
    return (
      <div className="cx-chead">
        <div className="cx-chead-l">
          <div className="cx-chead-av" style={{ background: "transparent", padding: 0, overflow: "hidden" }}>
            <Sk w="100%" h="100%" r={16} style={{ display: "block" }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div className="cx-chead-namerow"><Sk w={220} h={24} r={7} /></div>
            <div className="cx-chead-meta" style={{ marginTop: 8 }}><Sk w={300} h={12} /></div>
          </div>
        </div>
        <div className="cx-chead-actions">{actions}</div>
      </div>
    );
  }
  return (
    <div className="cx-chead">
      <div className="cx-chead-l">
        <div className="cx-chead-av lhc-reveal" style={{ background: gradient }}>{initialsOf(name)}</div>
        <div style={{ minWidth: 0 }} className="lhc-reveal">
          <div className="cx-chead-namerow">
            <h1 className="cx-chead-name">{name || "—"}</h1>
            <span className="cx-badge cx-badge-active">{statusLabel}</span>
          </div>
          <div className="cx-chead-meta">
            {metaItems.map((m, i) => <span key={i} className="cx-chead-meta-i">{m.icon}{m.text}</span>)}
          </div>
        </div>
      </div>
      <div className="cx-chead-actions">{actions}</div>
    </div>
  );
}

/* ── ClientTabs ───────────────────────────────────────────────────────── */
export function ClientTabs({ tabs, active, onChange }) {
  return (
    <div className="cx-tabs" role="tablist">
      {tabs.map((t) => (
        <button key={t.key} className={`cx-tab${active === t.key ? " active" : ""}`} role="tab" aria-selected={active === t.key}
          onClick={() => onChange(t.key)} data-testid={`tab-${t.key}`}>{t.label}</button>
      ))}
    </div>
  );
}

/* ── ProjectCard ──────────────────────────────────────────────────────── */
export function ProjectCard({ project, status, value, modules, updated, onOpen, onDelete }) {
  return (
    <div className="cx-card cx-pc" data-testid={`project-card-${project.id}`} onClick={onOpen} title="Open in the configurator">
      <div className="cx-pc-top">
        <div className="cx-pc-name">{project.name}</div>
        <span className="cx-pc-status" style={{ background: `${status.color}1a`, color: status.color }}>{status.label}</span>
      </div>
      <div className="cx-pc-modules">{Icon.cube(13)}{modules} module{modules === 1 ? "" : "s"}</div>
      <div className="cx-pc-prog">
        <div className="cx-pc-prog-bar"><div className="cx-pc-prog-fill" style={{ width: `${status.progress}%`, background: status.color }} /></div>
        <span className="cx-pc-prog-pct">{status.progress}%</span>
      </div>
      <div className="cx-pc-meta">
        <div>
          <div className="cx-pc-val-l">{money0(value)}</div>
          <div className="cx-pc-val-s">Total value</div>
        </div>
        {updated && <span className="cx-pc-date">{Icon.clock(12)}Updated {updated}</span>}
      </div>
      <div className="cx-pc-foot">
        <span className="cx-pc-open">Open{Icon.open(13)}</span>
        <div className="cx-pc-icons">
          <button className="cx-pc-iconbtn" title="Open in 3D" onClick={(e) => { e.stopPropagation(); onOpen(); }}>{Icon.cube(14)}</button>
          <button className="cx-pc-iconbtn danger" title="Delete project" onClick={(e) => { e.stopPropagation(); onDelete(); }}>{Icon.trash(14)}</button>
        </div>
      </div>
    </div>
  );
}

/* ── ProjectSummaryPanel (summary rows + mini revenue + top projects) ─── */
export function ProjectSummaryPanel({ rows = [], dropdown, revenue, topProjects, onTopItem, onViewFull, loading = false }) {
  return (
    <aside className="cx-side">
      <div className="cx-card cx-sum">
        <div className="cx-sum-head">
          <span className="cx-sum-t">Project summary</span>
          {dropdown}
        </div>
        {rows.map((r, i) => (
          <div key={i} className="cx-sum-row">
            <div className="cx-sum-ic" style={{ background: `var(--cx-${r.tone}-soft)`, color: `var(--cx-${r.tone})` }}>{r.icon}</div>
            <span className="cx-sum-label">{r.label}</span>
            <span className="cx-sum-val">{loading ? <Sk w={40} h={14} r={5} /> : <span className="lhc-reveal">{r.value}</span>}</span>
          </div>
        ))}
      </div>

      {revenue && (
        <div className="cx-card cx-mini">
          <div className="cx-mini-head"><span className="cx-mini-t">Revenue overview</span></div>
          {loading ? (
            <>
              <Sk w={110} h={22} r={7} style={{ margin: "2px 0 10px", display: "block" }} />
              <SkRows n={1} h={88} r={10} />
            </>
          ) : (
            <div className="lhc-reveal">
              <div className="cx-mini-total">
                <span className="cx-mini-amt">{money0(revenue.total)}</span>
                {revenue.trend ? <TrendBadge trend={revenue.trend} /> : null}
              </div>
              <RevenueChart data={revenue.data} mini />
            </div>
          )}
        </div>
      )}

      {topProjects && (
        <TopClientsCard loading={loading} title="Top projects by value" icon={Icon.chart(16)} items={topProjects} ranked
          onItem={onTopItem} onViewAll={onViewFull} viewAllLabel="View full overview" emptyHint="No project values yet." />
      )}
    </aside>
  );
}
